// src/components/pages/PrivacyPolicy.jsx
import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useSelector } from 'react-redux';
import { 
  FaShieldAlt, FaLock, FaUserSecret, FaCookieBite,
  FaEnvelope, FaPhone, FaMapMarkerAlt, FaGlobe,
  FaCheckCircle, FaArrowLeft, FaHome, FaFileAlt,
  FaDatabase, FaShareAlt, FaUserShield, FaServer,
  FaUtensils, FaHeart
} from 'react-icons/fa';
import { getColors } from '../../styles/colors';

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const theme = useSelector((state) => state.theme.mode);
  const isDark = theme === 'dark';
  const colors = getColors(isDark);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5 }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const sections = [
    {
      id: 'introduction',
      icon: <FaShieldAlt />,
      title: 'Introduction',
      content: `
        At Foodies Tiffin, we take your privacy seriously. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and use our tiffin delivery services.
        
        Please read this Privacy Policy carefully. By using our services, you consent to the data practices described in this policy.
      `
    },
    {
      id: 'information-collect',
      icon: <FaDatabase />,
      title: 'Information We Collect',
      content: `
        We collect information that you provide directly to us, including:
        
        • Personal identification information (name, email address, phone number, delivery address)
        • Payment information (processed securely through our payment partners)
        • Order history and preferences
        • Communication preferences and feedback
        • Device and usage information when you interact with our website
        • Location data to provide accurate delivery services
      `
    },
    {
      id: 'use-information',
      icon: <FaServer />,
      title: 'How We Use Your Information',
      content: `
        We use the information we collect to:
        
        • Process and deliver your tiffin orders
        • Communicate with you about orders, updates, and promotions
        • Improve our services and user experience
        • Personalize your experience with recommendations
        • Ensure compliance with legal obligations
        • Prevent fraudulent activities and enhance security
      `
    },
    {
      id: 'sharing',
      icon: <FaShareAlt />,
      title: 'Information Sharing',
      content: `
        We do not sell, trade, or rent your personal information to third parties. We may share your information:
        
        • With delivery partners to fulfill your orders
        • With service providers who assist in our operations
        • When required by law or to protect our rights
        • With your consent for specific purposes
        
        All third-party service providers are contractually obligated to protect your information.
      `
    },
    {
      id: 'security',
      icon: <FaLock />,
      title: 'Data Security',
      content: `
        We implement appropriate technical and organizational measures to protect your personal information:
        
        • SSL encryption for all data transmission
        • Secure payment processing through trusted partners
        • Regular security assessments and updates
        • Access controls and authentication measures
        • Data backup and recovery procedures
        
        While we strive to protect your information, no method of transmission over the internet is 100% secure.
      `
    },
    {
      id: 'cookies',
      icon: <FaCookieBite />,
      title: 'Cookies & Tracking',
      content: `
        We use cookies and similar tracking technologies to:
        
        • Remember your preferences and settings
        • Analyze website traffic and usage patterns
        • Personalize content and recommendations
        • Improve website performance and user experience
        
        You can control cookie preferences through your browser settings.
      `
    },
    {
      id: 'third-party',
      icon: <FaGlobe />,
      title: 'Third-Party Services',
      content: `
        Our website may contain links to third-party services. We are not responsible for:
        
        • The privacy practices of third-party websites
        • The content or accuracy of third-party information
        • Data collected by third-party services
        
        We encourage you to review the privacy policies of any third-party services you use.
      `
    },
    {
      id: 'user-rights',
      icon: <FaUserShield />,
      title: 'Your Rights',
      content: `
        You have the right to:
        
        • Access your personal information
        • Correct inaccurate or incomplete information
        • Request deletion of your data
        • Opt-out of marketing communications
        • Withdraw consent at any time
        • Data portability
        
        To exercise these rights, please contact us using the information below.
      `
    },
    {
      id: 'children',
      icon: <FaUserSecret />,
      title: "Children's Privacy",
      content: `
        Our services are not directed to individuals under the age of 13. We do not knowingly collect personal information from children. If we become aware that we have collected personal information from a child without parental consent, we will take steps to remove that information.
      `
    },
    {
      id: 'changes',
      icon: <FaFileAlt />,
      title: 'Changes to This Policy',
      content: `
        We may update this Privacy Policy from time to time. We will notify you of any changes by:
        
        • Posting the new policy on this page
        • Sending you an email notification
        • Displaying a notice on our website
        
        We encourage you to review this policy periodically for updates.
      `
    }
  ];

  return (
    <div className="min-h-screen py-8 px-4" style={{ backgroundColor: colors.background }}>
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <div className="mb-6">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-sm font-medium transition-all hover:scale-105"
            style={{ color: colors.primary }}
          >
            <FaArrowLeft className="text-xs" /> Back
          </button>
        </div>

        {/* Header */}
        <motion.div 
          className="text-center mb-10"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="inline-flex items-center gap-2 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm px-4 py-2 rounded-full text-xs font-medium shadow-sm mb-4" style={{ color: colors.primary }}>
            <FaLock className="text-[10px]" /> Privacy Policy
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold mb-3" style={{ color: colors.text }}>
            Privacy Policy
          </h1>
          <p className="text-sm" style={{ color: colors.textSecondary }}>
            Last Updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
          <div className="flex items-center justify-center gap-4 mt-4 text-xs" style={{ color: colors.textSecondary }}>
            <span className="flex items-center gap-1">
              <FaShieldAlt className="text-[10px]" style={{ color: colors.primary }} /> 
              Your Privacy Matters
            </span>
            <span className="w-px h-4" style={{ backgroundColor: colors.border }}></span>
            <span className="flex items-center gap-1">
              <FaCheckCircle className="text-[10px]" style={{ color: colors.primary }} /> 
              Secure & Transparent
            </span>
          </div>
        </motion.div>

        {/* Quick Navigation */}
        <motion.div 
          className="flex flex-wrap gap-2 mb-8 p-4 rounded-xl"
          style={{ backgroundColor: colors.card, border: `1px solid ${colors.border}` }}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
        >
          <span className="text-xs font-medium flex items-center gap-1.5" style={{ color: colors.textSecondary }}>
            <FaGlobe className="text-[10px]" /> Jump to:
          </span>
          {sections.map((section) => (
            <a
              key={section.id}
              href={`#${section.id}`}
              className="text-[10px] px-2.5 py-1 rounded-full transition-all hover:scale-105"
              style={{ 
                backgroundColor: colors.inputBackground,
                color: colors.textSecondary,
                border: `1px solid ${colors.border}`
              }}
            >
              {section.title}
            </a>
          ))}
        </motion.div>

        {/* Privacy Sections */}
        <motion.div 
          className="space-y-6"
          variants={staggerContainer}
          initial="initial"
          animate="animate"
        >
          {sections.map((section, index) => (
            <motion.div
              key={section.id}
              id={section.id}
              className="rounded-2xl overflow-hidden transition-all hover:shadow-md"
              style={{ 
                backgroundColor: colors.card,
                border: `1px solid ${colors.border}`,
              }}
              variants={fadeInUp}
            >
              <div className="p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: colors.primary + '20', color: colors.primary }}>
                    {section.icon}
                  </div>
                  <h2 className="text-lg font-bold" style={{ color: colors.text }}>
                    {section.title}
                  </h2>
                </div>
                <div className="text-sm leading-relaxed whitespace-pre-line" style={{ color: colors.textSecondary }}>
                  {section.content}
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Contact Information */}
        <motion.div 
          className="mt-8 p-6 rounded-2xl text-center"
          style={{ 
            backgroundColor: colors.card,
            border: `2px solid ${colors.primary}20`,
          }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
        >
          <h3 className="text-lg font-bold mb-2" style={{ color: colors.text }}>
            <FaEnvelope className="inline mr-2" style={{ color: colors.primary }} />
            Contact Us
          </h3>
          <p className="text-sm mb-4" style={{ color: colors.textSecondary }}>
            If you have any questions about this Privacy Policy, please contact us:
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4 text-sm">
            <span className="flex items-center gap-2" style={{ color: colors.textSecondary }}>
              <FaEnvelope style={{ color: colors.primary }} /> 
              privacy@foodiestiffin.com
            </span>
            <span className="w-px h-4" style={{ backgroundColor: colors.border }}></span>
            <span className="flex items-center gap-2" style={{ color: colors.textSecondary }}>
              <FaPhone style={{ color: colors.primary }} /> 
              +1 (555) 123-4567
            </span>
            <span className="w-px h-4" style={{ backgroundColor: colors.border }}></span>
            <span className="flex items-center gap-2" style={{ color: colors.textSecondary }}>
              <FaMapMarkerAlt style={{ color: colors.primary }} /> 
              123 Foodie Lane, Tiffin City
            </span>
          </div>
        </motion.div>

        {/* Footer */}
        <motion.div 
          className="mt-8 text-center text-xs"
          style={{ color: colors.textSecondary + '80' }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.8 }}
        >
          <p className="flex items-center justify-center gap-1">
            <FaUtensils className="text-[10px]" style={{ color: colors.primary }} />
            © {new Date().getFullYear()} Foodies Tiffin Service. All rights reserved.
            <FaHeart className="text-[10px]" style={{ color: '#EF4444' }} />
          </p>
          <div className="flex items-center justify-center gap-4 mt-2">
            <Link to="/" className="hover:underline" style={{ color: colors.primary }}>
              <FaHome className="inline mr-1 text-[10px]" /> Home
            </Link>
            <span className="w-px h-3" style={{ backgroundColor: colors.border }}></span>
            <Link to="/terms" className="hover:underline" style={{ color: colors.primary }}>
              Terms of Service
            </Link>
            <span className="w-px h-3" style={{ backgroundColor: colors.border }}></span>
            <Link to="/privacy" className="hover:underline" style={{ color: colors.primary }}>
              Privacy Policy
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;